import { useCallback, useEffect, useState } from 'react'
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  ShieldAlert,
  Siren,
  Wrench,
} from 'lucide-react'
import { useGetDrivingConduct, useRecordServiceInstruction, useRecordServiceReason } from '../hooks/backend/drivers'
import { useCapabilities } from '../hooks/useCapabilities'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { Label } from '../lib/shadcn/label'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Spinner } from '../lib/shadcn/spinner'
import { toast } from '../lib/shadcn/sonner'
import { Chip } from '../components/interviews/Chip'
import { DrivingCaseDialog } from '../components/drivers/DrivingCaseDialog'
import { AgreementSignaturesPanel } from '../components/drivers/AgreementSignaturesPanel'
import { DrivingFinesPanel } from '../components/drivers/DrivingFinesPanel'
import { writeErrorMessage } from '../utils/writeError'
import { readableDate } from '../utils/coachingDates'
import {
  CATEGORY_LABEL,
  INSTRUCTION_CHANNELS,
  KIND_LABEL,
  STEP_LABEL,
  UNATTRIBUTED_LABEL,
  dayLabel,
  isAgreementSetting,
  limitsLine,
  sastStamp,
  seconds,
  shiftWeek,
  speedLine,
  standingLabel,
  stepTone,
  type DrivingConductPage,
  type ServiceRow,
} from '../utils/drivingConduct'

/**
 * Driving conduct: clauses 21.5 to 21.7 of the v6 agreement and Addendum No. 1,
 * and the missed designated service of clause 20.10.
 *
 * Every figure on this page comes from driving_conduct_page_v1
 * (db/migrations/0374): the events are rebuilt from Cartrack's own readings,
 * attributed from shift records, and judged under the settings in force on the
 * day. This page decides nothing. It shows the week, and it is where a Driver
 * Servant records the three steps of a case.
 *
 * THE PLATFORM NEVER ISSUES A WARNING. A breach raises a case and a task; the
 * case moves only when a person records the written notice, the driver's
 * response and a decision. A dismissal is never a button: the heaviest thing
 * this page can do is refer a case to a hearing.
 *
 * COACHING ONLY UNTIL SIGNED. A driver who has not signed v6 or Addendum No. 1
 * is shown their events, and no case can be raised on them.
 */
export default function DrivingConduct() {
  const { loading: accessLoading, canView } = useCapabilities()
  const [week, setWeek] = useState<string | null>(null)
  const [view, setView] = useState<'week' | 'fines' | 'signatures'>('week')
  const [openCase, setOpenCase] = useState<string | null>(null)
  const [showUnattributed, setShowUnattributed] = useState(false)
  const [serviceForm, setServiceForm] = useState<{ row: ServiceRow; kind: 'instruction' | 'reason' } | null>(null)

  const page = useGetDrivingConduct<DrivingConductPage>()
  const trigger = page.trigger
  const load = useCallback(() => {
    trigger({ week_start: week }, { skipCache: true })
  }, [trigger, week])

  useEffect(() => {
    if (canView) load()
  }, [canView, load])

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }
  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <ShieldAlert className="h-6 w-6 text-[hsl(var(--brand))]" />
          <p className="text-sm font-medium text-foreground">
            Driving conduct is for Fleetly staff. If you should be able to see it, ask an admin to give your login view
            access.
          </p>
        </div>
      </div>
    )
  }

  const d = page.data
  const nobodySigned = d ? d.gate.drivers_signed === 0 : false
  // v6 first: it is the agreement, the addendum brings its clauses to older ones.
  const undated = d ? d.gate.agreements.filter((a) => !a.in_force_from).sort((a) => (a.is_addendum ? 1 : -1)) : []

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto bg-card">
      <div className="border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <Siren className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Driving conduct</h1>
          <div className="flex rounded-md border border-border p-0.5">
            {(['week', 'fines', 'signatures'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={
                  'rounded px-3 py-1 text-sm font-medium transition-colors ' +
                  (view === v ? 'bg-[hsl(var(--brand))] text-white' : 'text-muted-foreground hover:text-foreground')
                }
              >
                {v === 'week' ? 'Events and cases' : v === 'fines' ? 'Fines' : 'Signatures'}
              </button>
            ))}
          </div>
          <div className={view === 'week' ? 'ml-auto flex items-center gap-1' : 'hidden'}>
            <Button size="sm" variant="outline" aria-label="Previous week" disabled={!d} onClick={() => d && setWeek(shiftWeek(d.week_start, -1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[11rem] text-center text-sm font-medium tabular-nums">
              {d ? `${dayLabel(d.week_start)} to ${dayLabel(d.week_end)}` : 'This week'}
            </span>
            <Button size="sm" variant="outline" aria-label="Next week" disabled={!d} onClick={() => d && setWeek(shiftWeek(d.week_start, 1))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setWeek(null)}>
              This week
            </Button>
            <Button size="sm" variant="ghost" aria-label="Refresh" onClick={load}>
              <RefreshCw className={page.loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            </Button>
          </div>
        </div>
        {d && (
          <p className="mt-1 text-xs text-muted-foreground">
            Events are rebuilt from Cartrack's own readings for each tracker trip. Last read {sastStamp(d.feed.last_read_at)};{' '}
            {d.feed.trips_waiting > 0
              ? `${d.feed.trips_waiting.toLocaleString()} trips still waiting, oldest ${sastStamp(d.feed.oldest_waiting)}, so recent figures may still grow.`
              : 'every trip is in.'}
            {d.feed.trips_failed > 0 && ` ${d.feed.trips_failed} trip pages could not be read from Cartrack and are not counted.`}
          </p>
        )}
        {d && limitsLine(d.feed.limits) && (
          <p className="mt-0.5 text-xs text-muted-foreground">
            {limitsLine(d.feed.limits)} A limit counts only where OpenStreetMap signs the road, and then the higher of its
            limit and Cartrack's applies. Road data © OpenStreetMap contributors.
          </p>
        )}
        {page.error && (
          <p className="mt-2 flex items-start gap-1.5 rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              The page could not be read. The server said: <span className="font-medium">{page.error}</span>
            </span>
          </p>
        )}
      </div>

      {view === 'signatures' && <AgreementSignaturesPanel />}
      {view === 'fines' && <DrivingFinesPanel />}

      {view === 'week' && !d && page.loading && (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      )}

      {view === 'week' && d && (
        <div className="space-y-6 px-4 py-4">
          {(nobodySigned || undated.length > 0) && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
              <p className="font-medium">Coaching only for now: no case can be raised yet.</p>
              <p className="mt-1 text-xs">
                A case needs a driver who signed the v6 agreement or Addendum No. 1, for events on or after the date
                they signed and the date it came into force.{' '}
                {undated.length > 0 && `${undated.map((a) => (a.version === 'v6' ? 'v6' : 'Addendum No. 1')).join(' and ')} ${undated.length > 1 ? 'have' : 'has'} no in-force date yet. `}
                {d.gate.drivers_signed} of {d.gate.drivers_active} drivers have a signature recorded.
              </p>
              <Button size="sm" variant="outline" className="mt-2" onClick={() => setView('signatures')}>
                Record signatures
              </Button>
            </div>
          )}

          <section>
            <h2 className="mb-2 text-sm font-semibold">The fleet this week</h2>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
              {[
                ['Speeding events', d.summary.speeding],
                ['Harsh braking', d.summary.harsh_braking],
                ['Reckless periods', d.summary.reckless],
                ['Drivers with events', d.summary.drivers_with_events],
                ['Unattributed', d.summary.unattributed],
                ['Drivers in breach', d.breaches.length],
              ].map(([label, n]) => (
                <div key={label as string} className="rounded-md border border-border p-3">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-xl font-semibold tabular-nums">{n as number}</p>
                </div>
              ))}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              A breach is more than {d.threshold} speeding event, or more than {d.threshold} harsh braking event, in any
              7 days. Unattributed events count against nobody.
            </p>
          </section>

          <section>
            <h2 className="mb-2 text-sm font-semibold">Drivers in breach</h2>
            {d.breaches.length === 0 ? (
              <p className="text-xs text-muted-foreground">No driver has a 7 day window in breach that touches this week.</p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-muted text-left text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1.5">Driver</th>
                      <th className="px-2 py-1.5">Worst 7 days</th>
                      <th className="px-2 py-1.5 text-right">Speeding</th>
                      <th className="px-2 py-1.5 text-right">Harsh braking</th>
                      <th className="px-2 py-1.5">The ladder would give</th>
                      <th className="px-2 py-1.5">Case</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.breaches.map((b) => (
                      <tr key={b.driver_id} className="border-t border-border">
                        <td className="px-2 py-1.5">
                          <div className="font-medium">{b.driver_name ?? 'Unknown driver'}</div>
                          <div className="text-muted-foreground">{b.manager_name ?? 'No Driver Servant'}</div>
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5">
                          {dayLabel(b.window_start)} to {dayLabel(b.window_end)}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{b.speeding}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{b.harsh_braking}</td>
                        <td className="px-2 py-1.5">
                          <Chip tone={stepTone(b.next_step)}>{b.next_step ? STEP_LABEL[b.next_step] : '—'}</Chip>
                        </td>
                        <td className="px-2 py-1.5 text-muted-foreground">
                          {b.can_become_case ? 'Can become a case' : 'Coaching only'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {d.reckless.length > 0 && (
            <section>
              <h2 className="mb-2 text-sm font-semibold">Reckless periods (clause 21.5, its own offence)</h2>
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-muted text-left text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1.5">When</th>
                      <th className="px-2 py-1.5">Driver</th>
                      <th className="px-2 py-1.5">Car</th>
                      <th className="px-2 py-1.5">Where</th>
                      <th className="px-2 py-1.5">Speed</th>
                      <th className="px-2 py-1.5">For</th>
                      <th className="px-2 py-1.5">Standing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.reckless.map((e) => (
                      <tr key={e.event_id} className="border-t border-border">
                        <td className="whitespace-nowrap px-2 py-1.5">
                          {dayLabel(e.event_date)} {e.event_time.slice(0, 5)}
                        </td>
                        <td className="px-2 py-1.5">{e.driver_name ?? 'Not attributed'}</td>
                        <td className="px-2 py-1.5">{e.plate ?? '—'}</td>
                        <td className="px-2 py-1.5">{e.place || '—'}</td>
                        <td className="whitespace-nowrap px-2 py-1.5">{speedLine(e)}</td>
                        <td className="whitespace-nowrap px-2 py-1.5">{seconds(e.duration_s)}</td>
                        <td className="px-2 py-1.5 text-muted-foreground">{standingLabel(e)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section>
            <h2 className="mb-2 text-sm font-semibold">Open cases</h2>
            {d.cases.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No driving conduct case is open. Cases are raised each morning at 06:30 for signed drivers only.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-muted text-left text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1.5">Driver</th>
                      <th className="px-2 py-1.5">Case</th>
                      <th className="px-2 py-1.5">Ladder</th>
                      <th className="px-2 py-1.5">Next action</th>
                      <th className="px-2 py-1.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {d.cases.map((c) => (
                      <tr key={c.case_id} className="border-t border-border">
                        <td className="px-2 py-1.5">
                          <div className="font-medium">{c.driver_name}</div>
                          <div className="text-muted-foreground">{c.manager_name ?? 'No Driver Servant'}</div>
                        </td>
                        <td className="px-2 py-1.5">
                          {CATEGORY_LABEL[c.category]}, {readableDate(c.period_start)}
                          {c.period_end !== c.period_start && ` to ${readableDate(c.period_end)}`}
                        </td>
                        <td className="px-2 py-1.5">
                          <Chip tone={stepTone(c.step)}>{c.step ? STEP_LABEL[c.step] : '—'}</Chip>
                        </td>
                        <td className="px-2 py-1.5">{c.next_action}</td>
                        <td className="px-2 py-1.5 text-right">
                          <Button size="sm" variant={c.can_act ? 'default' : 'outline'} onClick={() => setOpenCase(c.case_id)}>
                            {c.can_act ? 'Open' : 'View'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {d.recently_decided.length > 0 && (
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer text-muted-foreground">Recently decided ({d.recently_decided.length})</summary>
                <ul className="mt-1 space-y-1">
                  {d.recently_decided.map((r) => (
                    <li key={r.case_id}>
                      <button type="button" className="underline" onClick={() => setOpenCase(r.case_id)}>
                        {r.driver_name}, {CATEGORY_LABEL[r.category]} {readableDate(r.period_start)}
                      </button>
                      : {(r.outcome ?? 'withdrawn').replace(/_/g, ' ')}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </section>

          <section>
            <h2 className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold">
              Unattributed events
              <span className="font-normal text-muted-foreground">{d.summary.unattributed} this week, counted against nobody</span>
            </h2>
            <div className="flex flex-wrap gap-2">
              {Object.entries(d.unattributed.by_reason).map(([reason, n]) => (
                <Chip key={reason} tone="neutral">
                  {UNATTRIBUTED_LABEL[reason] ?? reason}
                  <span className="ml-1 font-semibold tabular-nums">{n}</span>
                </Chip>
              ))}
            </div>
            {d.unattributed.events.length > 0 && (
              <Button size="sm" variant="ghost" className="mt-1 px-0 text-xs" onClick={() => setShowUnattributed(!showUnattributed)}>
                {showUnattributed ? 'Hide the list' : 'Show the list'}
              </Button>
            )}
            {showUnattributed && (
              <div className="mt-1 overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-muted text-left text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1.5">When</th>
                      <th className="px-2 py-1.5">What</th>
                      <th className="px-2 py-1.5">Car</th>
                      <th className="px-2 py-1.5">Where</th>
                      <th className="px-2 py-1.5">Speed</th>
                      <th className="px-2 py-1.5">Why nobody</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.unattributed.events.map((e) => (
                      <tr key={e.event_id} className="border-t border-border">
                        <td className="whitespace-nowrap px-2 py-1.5">
                          {dayLabel(e.event_date)} {e.event_time.slice(0, 5)}
                        </td>
                        <td className="px-2 py-1.5">{KIND_LABEL[e.kind]}</td>
                        <td className="px-2 py-1.5">{e.plate ?? '—'}</td>
                        <td className="px-2 py-1.5">{e.place || '—'}</td>
                        <td className="whitespace-nowrap px-2 py-1.5">{speedLine(e)}</td>
                        <td className="px-2 py-1.5 text-muted-foreground">{UNATTRIBUTED_LABEL[e.reason ?? ''] ?? e.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <Wrench className="h-4 w-4" /> Designated services, 30 days either side
            </h2>
            {d.services.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No service booking falls in the 30 days either side of today. A missed service can only become a case
                where a named driver was instructed in writing.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-muted text-left text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1.5">When</th>
                      <th className="px-2 py-1.5">Car and place</th>
                      <th className="px-2 py-1.5">Instructed in writing</th>
                      <th className="px-2 py-1.5">State</th>
                      <th className="px-2 py-1.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {d.services.map((s) => (
                      <tr key={s.booking_id} className="border-t border-border">
                        <td className="whitespace-nowrap px-2 py-1.5">{sastStamp(s.scheduled_at)}</td>
                        <td className="px-2 py-1.5">
                          {s.plate ?? '—'}, {s.place}
                        </td>
                        <td className="px-2 py-1.5">
                          {s.instructed_driver_name
                            ? `${s.instructed_driver_name} by ${s.instruction_channel}, ${sastStamp(s.instructed_at)}`
                            : 'Nobody yet'}
                        </td>
                        <td className="px-2 py-1.5">
                          {!s.is_designated
                            ? 'Not confirmed with a place and time'
                            : s.missed
                              ? `Missed${s.no_case_reason ? `: ${s.no_case_reason}` : ''}`
                              : s.valid_reason_before
                                ? `Reason recorded: ${s.reason_label}`
                                : s.status}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-right">
                          <Button size="sm" variant="outline" onClick={() => setServiceForm({ row: s, kind: 'instruction' })}>
                            Instruction
                          </Button>{' '}
                          <Button size="sm" variant="outline" onClick={() => setServiceForm({ row: s, kind: 'reason' })}>
                            Reason
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {serviceForm && (
              <ServiceForm
                key={`${serviceForm.row.booking_id}-${serviceForm.kind}`}
                row={serviceForm.row}
                kind={serviceForm.kind}
                options={d.options}
                onDone={() => {
                  setServiceForm(null)
                  load()
                }}
                onCancel={() => setServiceForm(null)}
              />
            )}
          </section>

          <section>
            <h2 className="mb-2 text-sm font-semibold">Settings in force</h2>
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted text-left text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5">Setting</th>
                    <th className="px-2 py-1.5 text-right">Value</th>
                    <th className="px-2 py-1.5">Since</th>
                    <th className="px-2 py-1.5">What it is</th>
                  </tr>
                </thead>
                <tbody>
                  {d.settings.map((s) => (
                    <tr key={s.key} className="border-t border-border">
                      <td className="px-2 py-1.5 font-mono">{s.key}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{Number(s.value)}</td>
                      <td className="whitespace-nowrap px-2 py-1.5">{readableDate(s.effective_from)}</td>
                      <td className="px-2 py-1.5">
                        {!isAgreementSetting(s) && <Chip tone="amber" className="mr-1">Fleetly's choice</Chip>}
                        {s.note}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Weekly event reports to drivers are {d.weekly_report_send ? 'switched on and go out every Monday at 07:00.' : 'written every Monday and shown on each driver record, but not emailed yet: sending is switched off.'}
            </p>
          </section>
        </div>
      )}

      <DrivingCaseDialog caseId={openCase} onClose={() => setOpenCase(null)} onChanged={load} />
    </div>
  )
}

function ServiceForm({
  row,
  kind,
  options,
  onDone,
  onCancel,
}: {
  row: ServiceRow
  kind: 'instruction' | 'reason'
  options: DrivingConductPage['options']
  onDone: () => void
  onCancel: () => void
}) {
  const instruct = useRecordServiceInstruction<{ booking_id: string }>()
  const reason = useRecordServiceReason<{ note: string }>()
  const [driverId, setDriverId] = useState('')
  const [instructedAt, setInstructedAt] = useState('')
  const [channel, setChannel] = useState('')
  const [evidence, setEvidence] = useState('')
  const [code, setCode] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  async function save() {
    setBusy(true)
    try {
      if (kind === 'instruction') {
        await instruct.trigger({
          booking_id: row.booking_id,
          driver_id: driverId,
          instructed_at: instructedAt ? `${instructedAt}:00+02:00` : '',
          channel,
          evidence,
        })
        toast.success('Instruction recorded')
      } else {
        const r = await reason.trigger({ booking_id: row.booking_id, reason_code: code, note })
        toast.success((r as { note?: string } | null)?.note ?? 'Reason recorded')
      }
      onDone()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <fieldset className="mt-2 space-y-2 rounded-md border border-border p-3 text-sm" disabled={busy}>
      <legend className="px-1 text-xs font-semibold">
        {kind === 'instruction' ? 'The written instruction' : 'A reason for not attending'}: {row.plate ?? 'car'},{' '}
        {sastStamp(row.scheduled_at)}
      </legend>
      {kind === 'instruction' ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <Label htmlFor="svc-driver">Driver instructed</Label>
            <NativeSelect id="svc-driver" value={driverId} onChange={(e) => setDriverId(e.target.value)}>
              <option value="">Choose</option>
              {options.drivers.map((o) => (
                <option key={o.driver_id} value={o.driver_id}>
                  {o.name}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div>
            <Label htmlFor="svc-at">Sent at (SAST)</Label>
            <Input id="svc-at" type="datetime-local" value={instructedAt} onChange={(e) => setInstructedAt(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="svc-channel">How</Label>
            <NativeSelect id="svc-channel" value={channel} onChange={(e) => setChannel(e.target.value)}>
              <option value="">Choose</option>
              {INSTRUCTION_CHANNELS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div>
            <Label htmlFor="svc-evidence">What proves it</Label>
            <Input id="svc-evidence" placeholder="The message, email subject or filed letter" value={evidence} onChange={(e) => setEvidence(e.target.value)} />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <Label htmlFor="svc-reason">Reason</Label>
            <NativeSelect id="svc-reason" value={code} onChange={(e) => setCode(e.target.value)}>
              <option value="">Choose</option>
              {options.reason_codes.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                  {r.is_valid_excuse ? '' : ' (not a valid excuse)'}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div>
            <Label htmlFor="svc-note">Note</Label>
            <Input id="svc-note" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
      )}
      <div className="flex gap-2">
        <Button size="sm" onClick={save}>
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </fieldset>
  )
}
